import React, { memo, useMemo } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    alpha,
    Chip,
    keyframes,
} from '@mui/material';
import { 
    Schedule, 
    Coffee, 
    AccessTime, 
    Groups,
    PlayArrow,
} from '@mui/icons-material';
import { TodaySchedule, SchedulePeriod } from '../../types/dashboardTypes';
import dateTimeUtils from '../../utils/dateTimeUtils';
import { getHashedColor } from '../../utils/colorUtils';
import {
    AccentChip,
    DashboardCard,
    DashboardEmptyState,
    DashboardSkeletonCard,
    PANEL_SKELETON_SX,
    useDashboardAccent,
    useDashboardTones,
} from './DashboardCard';

// Pulse animation for current period
const pulseRing = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.15); opacity: 0.5; }
    100% { transform: scale(1); opacity: 1; }
`;

// Gradient flow animation
const gradientFlow = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

interface TodayScheduleCardProps {
    data?: TodaySchedule;
    loading?: boolean;
}

/**
 * Today's Schedule Card
 * Elegant, compact timeline design for displaying teacher's daily schedule
 */
const TodayScheduleCard: React.FC<TodayScheduleCardProps> = memo(({
    data,
    loading = false,
}) => {
    const { theme, isDark, ink } = useDashboardTones();
    const live = useDashboardAccent('success');
    const breakTone = useDashboardAccent('warning').main;

    // Consistent colour per subject, from the app's shared categorical palette
    const getSubjectColor = (name: string) => getHashedColor(name, isDark).text;

    // Current period detection
    const currentPeriodIndex = useMemo(() => {
        if (!data?.periods.length) return -1;
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        for (let i = 0; i < data.periods.length; i++) {
            const period = data.periods[i];
            if (currentTime >= period.startTime && currentTime <= period.endTime) {
                return i;
            }
        }
        return -1;
    }, [data?.periods]);

    const formatTime = dateTimeUtils.formatClockTime;

    // Loading skeleton
    if (loading) {
        return (
            <DashboardSkeletonCard sx={[PANEL_SKELETON_SX, { p: 2.5, minHeight: 320 }]}>
                <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
                {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="rounded" height={44} sx={{ borderRadius: 2 }} />
                        </Box>
                    </Box>
                ))}
            </DashboardSkeletonCard>
        );
    }

    const periods = data?.periods || [];
    const hasData = periods.length > 0;

    return (
        <DashboardCard
            accent="success"
            icon={Schedule}
            title="Today's Schedule"
            subtitle={`${periods.length} ${periods.length === 1 ? 'class' : 'classes'} scheduled`}
            actions={data?.dayName && (
                <AccentChip
                    accent="success"
                    label={data.dayName}
                    sx={{ fontWeight: 600, fontSize: 11, height: 24 }}
                />
            )}
        >
            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {hasData ? (
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            py: 1.5,
                            px: 2,
                            // Minimal scrollbar
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                background: alpha(live.main, 0.2),
                                borderRadius: 2,
                                '&:hover': { background: alpha(live.main, 0.35) },
                            },
                            scrollbarWidth: 'thin',
                            scrollbarColor: `${alpha(live.main, 0.2)} transparent`,
                        }}
                    >
                        {periods.map((period: SchedulePeriod, index: number) => {
                            const isCurrent = index === currentPeriodIndex;
                            const isPast = currentPeriodIndex > -1 && index < currentPeriodIndex;
                            const isBreak = period.isBreak;
                            const subjectColor = !isBreak ? getSubjectColor(period.subjectName || 'Subject') : undefined;

                            return (
                                <Box
                                    key={`${period.slotNumber}-${period.startTime}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        mb: index < periods.length - 1 ? 0.75 : 0,
                                        opacity: isPast ? 0.5 : 1,
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {/* Time Column */}
                                    <Box
                                        sx={{
                                            width: 52,
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            justifyContent: 'center',
                                            pr: 1.5,
                                            py: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: 10,
                                                fontWeight: isCurrent ? 700 : 500,
                                                color: isCurrent ? live.main : 'text.secondary',
                                                letterSpacing: 0.3,
                                            }}
                                        >
                                            {formatTime(period.startTime).split(' ')[0]}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: 8,
                                                fontWeight: 500,
                                                color: isCurrent ? live.main : 'text.disabled',
                                                letterSpacing: 0.2,
                                            }}
                                        >
                                            {formatTime(period.startTime).split(' ')[1]}
                                        </Typography>
                                    </Box>

                                    {/* Timeline Connector */}
                                    <Box
                                        sx={{
                                            width: 20,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            position: 'relative',
                                        }}
                                    >
                                        {/* Top line */}
                                        {index > 0 && (
                                            <Box
                                                sx={{
                                                    width: 2,
                                                    flex: 1,
                                                    background: isCurrent 
                                                        ? `linear-gradient(to bottom, ${alpha(live.main, 0.3)}, ${live.main})`
                                                        : ink(0.08),
                                                }}
                                            />
                                        )}
                                        {/* Timeline dot */}
                                        <Box
                                            sx={{
                                                width: isCurrent ? 12 : 8,
                                                height: isCurrent ? 12 : 8,
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                background: isCurrent 
                                                    ? live.main
                                                    : isBreak 
                                                        ? breakTone
                                                        : isPast 
                                                            ? alpha(live.main, 0.4)
                                                            : ink(isDark ? 0.2 : 0.12),
                                                boxShadow: isCurrent ? `0 0 0 4px ${alpha(live.main, 0.2)}` : 'none',
                                                animation: isCurrent ? `${pulseRing} 2s ease-in-out infinite` : 'none',
                                                position: 'relative',
                                                zIndex: 1,
                                            }}
                                        />
                                        {/* Bottom line */}
                                        {index < periods.length - 1 && (
                                            <Box
                                                sx={{
                                                    width: 2,
                                                    flex: 1,
                                                    background: isCurrent 
                                                        ? `linear-gradient(to bottom, ${live.main}, ${alpha(live.main, 0.3)})`
                                                        : ink(0.08),
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Content Card */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            ml: 1.5,
                                            py: 0.75,
                                        }}
                                    >
                                        {isBreak ? (
                                            /* Break - Compact inline style */
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    borderRadius: 2,
                                                    background: alpha(breakTone, isDark ? 0.1 : 0.08),
                                                    border: `1px dashed ${alpha(breakTone, 0.3)}`,
                                                }}
                                            >
                                                <Coffee sx={{ fontSize: 14, color: breakTone }} />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: breakTone,
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    {period.breakName || 'Break'}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: alpha(breakTone, 0.7),
                                                        fontSize: 10,
                                                        ml: 'auto',
                                                    }}
                                                >
                                                    {formatTime(period.endTime)}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            /* Class Period - Modern compact card */
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 1,
                                                    borderRadius: 2,
                                                    background: isCurrent
                                                        ? isDark 
                                                            ? `linear-gradient(135deg, ${alpha(live.main, 0.12)} 0%, ${alpha(live.dark, 0.08)} 100%)`
                                                            : `linear-gradient(135deg, ${alpha(live.main, 0.08)} 0%, ${alpha(live.main, 0.04)} 100%)`
                                                        : ink(isDark ? 0.03 : 0.02),
                                                    border: '1px solid',
                                                    borderColor: isCurrent 
                                                        ? alpha(live.main, isDark ? 0.25 : 0.2)
                                                        : 'transparent',
                                                    borderLeft: `3px solid ${subjectColor}`,
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'default',
                                                    '&:hover': {
                                                        background: isCurrent
                                                            ? isDark 
                                                                ? `linear-gradient(135deg, ${alpha(live.main, 0.15)} 0%, ${alpha(live.dark, 0.1)} 100%)`
                                                                : `linear-gradient(135deg, ${alpha(live.main, 0.1)} 0%, ${alpha(live.main, 0.06)} 100%)`
                                                            : ink(isDark ? 0.05 : 0.04),
                                                        transform: 'translateX(2px)',
                                                    },
                                                    // Current period special animation
                                                    ...(isCurrent && {
                                                        animation: `${gradientFlow} 3s ease infinite`,
                                                        backgroundSize: '200% 200%',
                                                    }),
                                                }}
                                            >
                                                {/* Top row: Subject + Current badge */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: subjectColor,
                                                            fontSize: 13,
                                                            letterSpacing: 0.2,
                                                        }}
                                                    >
                                                        {period.subjectName || 'Subject TBD'}
                                                    </Typography>
                                                    {isCurrent && (
                                                        <Chip
                                                            icon={<PlayArrow sx={{ fontSize: '12px !important' }} />}
                                                            label="LIVE"
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: 9,
                                                                fontWeight: 700,
                                                                letterSpacing: 0.5,
                                                                background: `linear-gradient(135deg, ${live.main} 0%, ${live.dark} 100%)`,
                                                                color: theme.palette.common.white,
                                                                '& .MuiChip-icon': {
                                                                    marginLeft: '4px',
                                                                    marginRight: '-4px',
                                                                    color: theme.palette.common.white,
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                
                                                {/* Bottom row: Meta info */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {/* Time */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTime sx={{ fontSize: 11, color: 'text.disabled' }} />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {formatTime(period.endTime)}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    {/* Class */}
                                                    {period.className && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Groups sx={{ fontSize: 11, color: 'text.disabled' }} />
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: 10,
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {period.className}{period.sectionName ? `-${period.sectionName}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <DashboardEmptyState
                        size="roomy"
                        accent="success"
                        icon={Schedule}
                        title="No Classes Today"
                        description={data?.dayName ? `Enjoy your ${data.dayName}!` : 'Your schedule will appear here.'}
                        sx={{ flex: 1, height: 'auto', py: 4 }}
                    />
                )}
            </Box>
        </DashboardCard>
    );
});

TodayScheduleCard.displayName = 'TodayScheduleCard';

export default TodayScheduleCard;
